/**
 * Work-focus gate for the admin console.
 *
 * Wraps the authenticated area and enforces three things:
 *
 * 1. **Enrolment before access.** An operator with no face template is shown the enrolment
 *    flow and cannot reach the console until it completes.
 * 2. **Monitoring without a monitoring screen.** The pipeline runs headless — there is no
 *    dashboard, only a small indicator in the top bar. The camera element is mounted but
 *    visually collapsed, because a `display: none` video is not guaranteed to deliver frames.
 * 3. **Lock on absence or a different face.** The session guard renders a full-screen overlay
 *    over the console; the session itself stays alive so no in-progress work is lost.
 *
 * On the deliberate absence of a consent screen: `privacy.requireAcknowledgement` is false by
 * operator decision, so the browser's own camera permission prompt is the only gate. The small
 * top-bar indicator is kept because monitoring that leaves no visible trace is a materially
 * different product from one that does, and the policy validator expects an indicator to exist.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import type { GuardDecision, LockReason, Policy } from '../core/index.js';
import { enrolmentStatus } from '../cv/identityClient';
import { fetchLock, reportLock, type RemoteLock } from '../cv/sessionLockClient';
import { useBrowserFocusPipeline } from '../hooks/useBrowserFocusPipeline';
import { FaceEnrolmentPage } from '../pages/FaceEnrolmentPage';
import { SessionLockOverlay } from './SessionLockOverlay';

/**
 * Session-guard settings for the console.
 *
 * Both locks are live at 15 seconds of sustained evidence.
 *
 * Identity locking was previously disabled because unrelated faces scored 0.59-0.91 cosine
 * against a 0.36 threshold — a mismatch verdict meant nothing. Two fixes since made the verdict
 * usable: client-side ArcFace alignment before embedding (`alignFaceToJpeg`), and median rather
 * than best-of-N aggregation, which had been carrying a previous person's high scores across a
 * swap. The threshold itself is still the default rather than an empirically calibrated one, so
 * treat a false lock as a threshold-tuning signal — `WORK_FOCUS_MIN_MATCH_SIMILARITY` on the
 * work-focus API is the dial, and `work-focus/scripts/calibrate-face-threshold.mjs` measures it.
 *
 * What makes shipping it before calibration acceptable is the cost of being wrong: the lock is an
 * overlay over a live session, so a false positive costs seconds and no work, and clearing it
 * needs both liveness *and* an identity match — see SessionLockOverlay.
 */
const GUARD_OVERRIDES: Partial<Policy['sessionGuard']> = {
  enabled: true,
  mode: 'enforce',
  // The three lock durations are deliberately NOT set here. They come from policy, which reads
  // both build-time `VITE_FOCUS_AGENT__*` and runtime `RUNTIME_FOCUS_AGENT__*` variables, so a
  // deployed environment can be retuned without rebuilding the image. Listing them here would
  // silently win over both and make those variables inert — exactly what happened with the match
  // threshold, where raising the server value had no effect because the client compared against
  // its own copy.
  //
  // `enabled` and `mode` stay above on purpose: runtime config arrives as a script the browser
  // fetches, so it is ultimately under the control of the person being monitored. Lock durations
  // are safe to expose — there is nothing to gain by lengthening your own dwell — but an on/off
  // switch for a monitoring control is not.
  //
  // Shipped defaults, from core/config.ts: face absent 15s, identity mismatch 20s,
  // camera unavailable 30s.
  //
  // Checked more often than the shipped default: at a 15s lock delay, a 180s interval could
  // never produce more than one sample inside the window, so a single bad frame decided the lock.
  identityCheckIntervalSeconds: 5,
  identityResultMaxAgeSeconds: 60,
  // `minMatchSimilarity` is deliberately absent. The server owns the operating point via
  // WORK_FOCUS_MIN_MATCH_SIMILARITY and returns it on every verify, and the pipeline uses that
  // value. Setting it here would recreate the split that made raising the server threshold a
  // no-op: the client compared the median against its own number and discarded the server's
  // verdict. The policy field survives only as a fallback for a server that does not report one.
};

export interface WorkFocusGateProps {
  /** Stable identifier for the signed-in operator; the face template is keyed to it. */
  subjectId: string | null | undefined;
  displayName?: string | undefined;
  /** When false, skip enrolment and headless monitoring for this operator. */
  monitoringEnabled?: boolean;
  /**
   * Sign the operator out, sending them to the password login.
   *
   * The escape hatch for a lock that face verification cannot clear — most obviously a
   * `camera_unavailable` lock, where the camera the overlay needs is the very thing that has
   * failed. Without it a broken webcam would strand somebody in an overlay with no way out but
   * closing the tab.
   */
  onSignOut?: () => void;
  children: ReactNode;
}

type Enrolment = 'checking' | 'required' | 'complete' | 'unavailable';

export function WorkFocusGate({
  subjectId,
  displayName,
  monitoringEnabled = true,
  onSignOut,
  children,
}: WorkFocusGateProps) {
  const [enrolment, setEnrolment] = useState<Enrolment>('checking');

  const check = useCallback(async () => {
    if (!subjectId || !monitoringEnabled) return;
    const status = await enrolmentStatus();
    if (!status.known) {
      // The service could not be reached. Falling open is right — this is session hardening,
      // not the primary authentication, which already succeeded. Crucially it is NOT treated as
      // "not enrolled": doing so would throw an already-enrolled operator into enrolment during
      // an outage and replace their template with a fresh capture.
      setEnrolment('unavailable');
      return;
    }
    setEnrolment(status.enrolled ? 'complete' : 'required');
  }, [subjectId, monitoringEnabled]);

  useEffect(() => {
    if (!monitoringEnabled) {
      setEnrolment('unavailable');
      return;
    }
    void check();
  }, [check, monitoringEnabled]);

  // Monitoring only runs once enrolment is settled, so the camera is not competing with the
  // enrolment flow for the device.
  const monitoringActive = monitoringEnabled && enrolment === 'complete';

  if (!subjectId || !monitoringEnabled) return <>{children}</>;

  if (enrolment === 'checking') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }} spacing={1.5}>
        <CircularProgress size={26} />
        <Typography variant="body2" color="text.secondary">
          Checking workstation verification…
        </Typography>
      </Stack>
    );
  }

  if (enrolment === 'required') {
    // A full-viewport layer, not an in-page panel. Rendered inside the console shell it left
    // the sidebar and navigation reachable, so the operator could simply click past the step
    // that is supposed to gate access.
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 2,
          bgcolor: 'background.default',
          overflowY: 'auto',
        }}
      >
        <FaceEnrolmentPage
          {...(displayName === undefined ? {} : { displayName })}
          onEnrolled={() => {
            // Re-read from the service rather than assuming success: the template is only real
            // once the server has stored it.
            void check();
          }}
        />
      </Box>
    );
  }

  return (
    <>
      {children}
      {monitoringActive ? (
        <HeadlessMonitor
          subjectId={subjectId}
          {...(onSignOut === undefined ? {} : { onSignOut })}
        />
      ) : null}
    </>
  );
}

/**
 * Runs the pipeline with no visible dashboard.
 *
 * The video element is mounted at 1x1 with zero opacity rather than `display: none`: a hidden
 * video may be denied frames by the browser, which would look identical to an absent employee
 * and lock them out.
 */
/**
 * Rebuild a decision object for a lock the server told us about.
 *
 * A lock restored after a page reload has no local guard decision behind it — the guard that
 * applied it lives in a process that is gone. The overlay needs the same shape either way, so the
 * server's reason and evidence are reassembled here. The elapsed-time counters are zero because
 * this process genuinely does not know them; inventing plausible numbers would be worse.
 */
function decisionFromRemote(remote: RemoteLock): GuardDecision {
  return {
    locked: true,
    reason: remote.reason ?? 'face_absent',
    wouldLock: true,
    evidence:
      remote.evidence.length > 0
        ? remote.evidence
        : ['this workstation was locked and has not been unlocked since'],
    faceAbsentForSeconds: 0,
    identityMismatchForSeconds: 0,
    cameraUnavailableForSeconds: 0,
    suspended: false,
  };
}

/**
 * Runs the pipeline with no visible dashboard, and holds the lock overlay.
 *
 * The video element is mounted at 1x1 with zero opacity rather than `display: none`: a hidden
 * video may be denied frames by the browser, which would look identical to an absent employee
 * and lock them out.
 *
 * Locks are **reported to and restored from admin-api**. Holding them only in this component was
 * a real weakness: a page reload built a fresh, unlocked guard and dropped straight into the
 * console, so the lock was a picture of a control rather than one. The server now also refuses
 * console data routes with 423 while a lock stands, which closes the second tab and direct-request
 * routes at the same time.
 */
function HeadlessMonitor({
  subjectId,
  onSignOut,
}: {
  subjectId: string;
  onSignOut?: () => void;
}) {
  /** Null while the server is still being asked. */
  const [remote, setRemote] = useState<RemoteLock | null>(null);

  useEffect(() => {
    void fetchLock().then(setRemote);
  }, []);

  const remoteLocked = remote?.locked === true;

  // Monitoring is disabled while a restored lock stands, so the camera is free for the overlay's
  // own liveness check rather than being contended for — see the pipeline hook.
  const { videoRef, guard, unlock, policy } = useBrowserFocusPipeline(!remoteLocked, {
    guardOverrides: GUARD_OVERRIDES,
    identitySubjectId: subjectId,
  });

  const mirrored = useMemo(() => policy.presentation.mirrorDisplay, [policy]);

  // Report a newly applied lock upstream, once per lock rather than on every render.
  const reportedReason = useRef<LockReason | null>(null);
  useEffect(() => {
    if (guard?.locked !== true) {
      reportedReason.current = null;
      return;
    }
    const reason = guard.reason ?? 'face_absent';
    if (reportedReason.current === reason) return;
    reportedReason.current = reason;
    void reportLock(reason, guard.evidence);
  }, [guard]);

  const handleUnlocked = useCallback(() => {
    // The server has already released the lock by this point — the overlay only calls this after
    // a successful clear — so the local guard is brought back in line with it.
    setRemote({ locked: false, reason: null, evidence: [], known: true });
    reportedReason.current = null;
    unlock();
  }, [unlock]);

  const decision: GuardDecision | null =
    guard?.locked === true ? guard : remoteLocked && remote !== null ? decisionFromRemote(remote) : null;

  return (
    <>
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        playsInline
        muted
        aria-hidden="true"
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          transform: mirrored ? 'scaleX(-1)' : 'none',
        }}
      />
      {decision !== null ? (
        <SessionLockOverlay
          decision={decision}
          onUnlocked={handleUnlocked}
          {...(onSignOut === undefined ? {} : { onUsePassword: onSignOut })}
        />
      ) : null}
    </>
  );
}
